import { ButtonColors } from '@chiroup/components/Button';
import { ConfirmModal } from '@chiroup/components/ConfirmModal';
import { OpenClosedStates } from '@chiroup/core/enums/OpenClosedStates.enum';
import { useState } from 'react';
import Modal from '../../../../common/Modal';
import InlineAssessmentInput from './InlineAssessmentInput';
import NeuroDropdown from './NeuroDropdown';

const mentalStatusRows = [
  {
    key: 'attention',
    text: 'Attention',
    default: 'Alert',
    type: 'dropdown',
    arr: ['Alert', 'Abnormal', 'Not assessed'],
  },
  {
    key: 'personPlaceTime',
    text: 'Person, place, and time',
    default: 'Oriented',
    type: 'dropdown',
    arr: ['Oriented', 'Abnormal', 'Not assessed'],
  },
  {
    key: 'speech',
    text: 'Speech',
    default: 'Clear and fluent',
    type: 'dropdown',
    arr: ['Clear and fluent', 'Abnormal', 'Not assessed'],
  },
  {
    key: 'memoryThoughtProcess',
    text: 'Memory and thought process',
    default: 'Intact',
    type: 'dropdown',
    arr: ['Intact', 'Abnormal', 'Not assessed'],
  },
];

const coordinationStatusRows = [
  {
    key: 'fingerToNoseTest',
    text: 'Finger to nose test',
    default: 'Normal',
    type: 'dropdown',
    arr: ['Normal', 'Abnormal', 'Not assessed'],
  },
  {
    key: 'heelToShinTest',
    text: 'Heel to shin test',
    default: 'Normal',
    type: 'dropdown',
    arr: ['Normal', 'Abnormal', 'Not assessed'],
  },
  {
    key: 'hautantsManeuver',
    text: `Hautant's maneuver`,
    default: 'No sign of drift',
    type: 'dropdown',
    arr: ['No sign of drift', 'Abnormal', 'Not assessed'],
  },
  {
    key: 'rhombergsSign',
    text: `Rhomberg sign`,
    default: 'Absent',
    type: 'dropdown',
    arr: ['Absent', 'Abnormal', 'Not assessed'],
  },
  {
    key: 'rapidAlternatingFingerMovements',
    text: 'Rapid alternating finger movements',
    default: 'Intact',
    type: 'dropdown',
    arr: ['Intact', 'Abnormal', 'Not assessed'],
  },
  {
    key: 'fineFingerMovements',
    text: 'Fine finger movements',
    default: 'Normal',
    type: 'dropdown',
    arr: ['Normal', 'Abnormal', 'Not assessed'],
  },
  {
    key: 'stanceBase',
    text: 'Stance base',
    default: 'Normal',
    type: 'dropdown',
    arr: ['Normal', 'Abnormal', 'Not assessed'],
  },
  {
    key: 'gait',
    text: 'Gait',
    default: 'Steady',
    type: 'dropdown',
    arr: ['Steady', 'Abnormal', 'Not assessed'],
  },
  {
    key: 'stepsAndTurning',
    text: 'Steps and turning',
    default: 'Normal',
    type: 'dropdown',
    arr: ['Normal', 'Abnormal', 'Not assessed'],
  },
  {
    key: 'heelWalkAndToeWalk',
    text: 'Heel walk and toe walk',
    default: 'Normal',
    type: 'dropdown',
    arr: ['Normal', 'Abnormal', 'Not assessed'],
  },
];

const pathologicStatusRows = [
  {
    key: 'ankleClonus',
    text: 'Ankle clonus',
    default: 'Absent',
    type: 'dropdown',
    arr: ['Absent', 'Abnormal', 'Not assessed'],
  },
  {
    key: 'babinskiSign',
    text: 'Babinski sign',
    default: 'Absent',
    type: 'dropdown',
    arr: ['Absent', 'Abnormal', 'Not assessed'],
  },
  {
    key: 'hoffmanSign',
    text: 'Hoffman sign',
    default: 'Absent',
    type: 'dropdown',
    arr: ['Absent', 'Abnormal', 'Not assessed'],
  },
];

type Props = {
  isOpen: boolean;
  close: () => void;
  value: any;
  onChange: (value?: any) => void;
  signed?: boolean;
};

const CentralEditableChart: React.FC<Props> = ({ isOpen, close, value, onChange, signed }) => {
  const [confirmOpen, setConfirmOpen] = useState<OpenClosedStates>(OpenClosedStates.Closed);
  const [inlineAbnormalInput, setInlineAbnormalInput] = useState<{
    category: string;
    test: string;
    value: string;
    onSubmit: (explanation: string) => void;
  } | null>(null);

  const clearValues = () => {
    if (signed) return;
    onChange({ result: value?.result });
  };

  const getOverallResult = (val: string, category: string, test: string) => {
    if (val === 'Abnormal') return 'abnormal';

    // Handle case where value or value.detail is undefined
    if (!value?.detail) return 'normal';

    const isAbnormal = Object.entries(value.detail)?.some(([innerCategory, categoryData]: any) => {
      const sameCategory = innerCategory === category;

      return Object.entries(categoryData)?.some(([innerTest, testData]: any) => {
        const sameTest = innerTest === test;
        if (sameCategory && sameTest) return false;

        return testData.choice === 'Abnormal';
      });
    });

    return isAbnormal ? 'abnormal' : 'normal';
  };

  const getResult = (val: string) => {
    const isAbnormal = val === 'Abnormal';

    if (isAbnormal) {
      return 'abnormal';
    }

    return 'normal';
  };

  const onChangeItem = (category: string, test: string) => (val: string) => {
    if (signed) return;
    const overallResult = getOverallResult(val, category, test);
    const result = getResult(val);
    const isAbnormal = result === 'abnormal';

    onChange({
      ...(value || {}),
      detail: {
        ...value?.detail,
        [category]: {
          ...value?.detail?.[category],
          [test]: { choice: val },
        },
      },
      result: overallResult,
    });

    if (isAbnormal) {
      setInlineAbnormalInput({
        value: value?.detail?.[category]?.[test]?.explanation || '',
        category,
        test,
        onSubmit: (explanation: string) => {
          onChange({
            ...(value || {}),
            detail: {
              ...value?.detail,
              [category]: {
                ...value?.detail?.[category],
                [test]: {
                  ...value?.detail?.[category]?.[test],
                  choice: val,
                  explanation,
                },
              },
            },
            result: overallResult,
          });
        },
      });

      return;
    }
  };

  const checkOverall = () => {
    if (!value?.detail) return 'normal';
    const isAbnormal = Object.entries(value.detail)?.some(([innerCategory, categoryData]: any) => {
      return Object.entries(categoryData)?.some(([innerTest, testData]: any) => {
        return testData.choice === 'Abnormal';
      });
    });

    return isAbnormal ? 'abnormal' : 'normal';
  };

  const closeModal = () => {
    const overAll = checkOverall();

    if (value?.result === overAll || signed) {
      close();

      return;
    }

    setConfirmOpen(OpenClosedStates.Open);
  };

  const confirm = () => {
    setConfirmOpen(OpenClosedStates.Closed);
    onChange({
      ...(value || {}),
      result: 'normal',
    });
    close();
  };

  return (
    <Modal
      isOpen={isOpen}
      close={closeModal}
      size="3xl"
      header={{
        title: 'Central Neurologic Test',
      }}
      footer={{
        buttons: [
          {
            text: 'Clear All Values',
            onClick: clearValues,
          },
          {
            text: 'Close',
            onClick: closeModal,
            color: ButtonColors.primary,
          },
        ],
      }}
    >
      <div className="w-full">
        <table className="min-w-[380px] overflow-x-auto divide-y divide-gray-300 w-full border">
          <tbody className=" dark:bg-dark-gray-900">
            {mentalStatusRows.map((item, i) => {
              return (
                <tr key={`ms${i}`}>
                  {i === 0 && (
                    <th className="w-1/5 border pl-2" rowSpan={4}>
                      Mental status
                    </th>
                  )}
                  <th className="w-2/5 border pl-2">{item.text}</th>
                  <td className="w-2/5  text-center border-b">
                    <div className="flex items-center justify-center w-full">
                      <NeuroDropdown
                        key={item.key}
                        row={item.key}
                        dropdownOptions={item.arr}
                        value={value?.detail?.mentalStatus?.[item.key]}
                        onChange={onChangeItem('mentalStatus', item.key)}
                        defaultVal={item.default}
                      />
                      {inlineAbnormalInput &&
                        inlineAbnormalInput.category === 'mentalStatus' &&
                        inlineAbnormalInput.test === item.key && (
                          <InlineAssessmentInput
                            value={inlineAbnormalInput.value}
                            inputClassName="w-full"
                            onChange={val =>
                              setInlineAbnormalInput({ ...inlineAbnormalInput, value: val })
                            }
                            onSubmit={() => {
                              inlineAbnormalInput.onSubmit(inlineAbnormalInput.value);
                              setInlineAbnormalInput(null);
                            }}
                          />
                        )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {coordinationStatusRows.map((item, i) => {
              return (
                <tr key={`coord${i}`}>
                  {i === 0 && (
                    <th className="w-1/5 border pl-2" rowSpan={10}>
                      Coordination
                    </th>
                  )}
                  <th className="w-2/5 border pl-2">{item.text}</th>
                  <td className="w-2/5 text-center border-b">
                    <NeuroDropdown
                      key={item.key}
                      row={item.key}
                      dropdownOptions={item.arr}
                      value={value?.detail?.coordination?.[item.key]}
                      onChange={onChangeItem('coordination', item.key)}
                      defaultVal={item.default}
                    />
                    {inlineAbnormalInput &&
                      inlineAbnormalInput.category === 'coordination' &&
                      inlineAbnormalInput.test === item.key && (
                        <InlineAssessmentInput
                          value={inlineAbnormalInput.value}
                          inputClassName="w-full"
                          onChange={val =>
                            setInlineAbnormalInput({ ...inlineAbnormalInput, value: val })
                          }
                          onSubmit={() => {
                            inlineAbnormalInput.onSubmit(inlineAbnormalInput.value);
                            setInlineAbnormalInput(null);
                          }}
                        />
                      )}
                  </td>
                </tr>
              );
            })}
            {pathologicStatusRows.map((item, i) => {
              return (
                <tr key={`path${i}`}>
                  {i === 0 && (
                    <th className="w-1/5 border pl-2" rowSpan={3}>
                      Pathologic reflexes
                    </th>
                  )}
                  <th className="w-2/5 border pl-2">{item.text}</th>
                  <td className="w-2/5 text-center border-b">
                    <NeuroDropdown
                      key={item.key}
                      row={item.key}
                      dropdownOptions={item.arr}
                      value={value?.detail?.pathologicReflexes?.[item.key]}
                      onChange={onChangeItem('pathologicReflexes', item.key)}
                      defaultVal={item.default}
                    />
                    {inlineAbnormalInput &&
                      inlineAbnormalInput.category === 'pathologicReflexes' &&
                      inlineAbnormalInput.test === item.key && (
                        <InlineAssessmentInput
                          value={inlineAbnormalInput.value}
                          inputClassName="w-full"
                          onChange={val =>
                            setInlineAbnormalInput({ ...inlineAbnormalInput, value: val })
                          }
                          onSubmit={() => {
                            inlineAbnormalInput.onSubmit(inlineAbnormalInput.value);
                            setInlineAbnormalInput(null);
                          }}
                        />
                      )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <ConfirmModal
          isOpen={confirmOpen === OpenClosedStates.Open}
          confirm={confirm}
          close={() => setConfirmOpen(OpenClosedStates.Closed)}
          confirmText="Confirm"
          description="No individual tests have been marked abnormal. The overall test result will be changed back to normal."
        />
      </div>
    </Modal>
  );
};

export default CentralEditableChart;
